import { FC, memo, useEffect, useRef, useState } from 'react'
import DocViewer, {
  DocViewerRenderers,
  IDocument,
} from '@cyntler/react-doc-viewer'
import '@cyntler/react-doc-viewer/dist/index.css'
import useEffectOnce from '@dx-system/react-use/lib/useEffectOnce'
import { useTranslation } from '@crew/modules/i18n'
import { HttpStatusCode } from '@crew/enums/system'

type CrewDocViewerProps = {
  documents: IDocument[]
}

export const CrewDocViewer: FC<CrewDocViewerProps> = memo((props) => {
  const { t } = useTranslation()
  const [error, setError] = useState<boolean>(false)

  const abortControllerRef = useRef<AbortController | null>(null)

  // コンポーネントアンマウント時にfetchを中止するためのAbortController
  useEffect(() => {
    return () => {
      if (abortControllerRef?.current) {
        // 既にabortControllerのインスタンスが存在する -> fetch中なので、それを中止する
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // プリフェッチ
  // CrewDocViewerのレンダラーがライブラリ内部で閉じ込められており、エラーメッセージの修正ができない
  // カスタムレンダラーも作成出来るが、対応するレンダラー（ https://github.com/cyntler/react-doc-viewer?tab=readme-ov-file#supported-file-types ）を作成し、そこにエラーハンドリングを仕込む必要がある
  // よって事前のfetchでエラーが出るか確認する
  useEffectOnce(() => {
    // ファイルの存在確認
    const checkFile = async () => {
      try {
        if (props.documents.length === 0) {
          // documentsが空の場合は何もしない
          return
        }

        abortControllerRef.current = new AbortController()

        const resp = await fetch(props.documents[0].uri, {
          method: 'GET',
          signal: abortControllerRef.current.signal,
          headers: {
            Range: 'bytes=0-0', // 先頭の1バイトだけ取得するようにリクエストを送る
          },
        })

        if (
          !resp.ok &&
          // 416はRange指定範囲が不正な場合に返ってくる(≒ファイルが0バイト)が、
          // ファイル自体は存在しているためここではエラーとしない
          resp.status !== HttpStatusCode.RequestedRangeNotSatisfiable
        ) {
          setError(true)
        }
      } catch (err) {
        console.debug('load office preview error:', err)

        // そもそもサーバーに到達できていないなどのネットワークエラーの場合
        if (err instanceof DOMException && err.name === 'AbortError') {
          // fetchがabortされた場合は何もしない
          return
        }

        setError(true)
      }
    }

    checkFile()
  })

  return error ? (
    <div>{t('label.previewGenerating')}</div>
  ) : (
    <DocViewer
      documents={props.documents}
      initialActiveDocument={props.documents[0]}
      pluginRenderers={DocViewerRenderers}
      prefetchMethod="GET"
      config={{
        header: {
          disableHeader: true,
          disableFileName: true,
        },
      }}
      className="crew-bg-default"
    />
  )
})
